import {
  DealVersion,
  PreciseDealPricingStatus,
  BasicDealPricingStatus,
  OrderPeriodType
} from '../../types/deal.types';
import { IssueOrderPeriod } from './order-period';
import { DealTypeVersion } from './type';
import moment, { Moment, MomentInput } from 'moment';
import { cloneDeep } from 'lodash';

export function getBasicDealPricingStatus(
  deal: DealVersion,
  nowInput?: MomentInput
): BasicDealPricingStatus {
  const now = moment(nowInput).local();
  const preciseStatus = getPreciseDealPricingStatus(deal, now);

  if (preciseStatus === PreciseDealPricingStatus.Pending) {
    return mapPendingStatusToBasic(deal, now);
  }

  return preciseStatus;
}

// used for Deal Manager(align grid for pending status to move under Live)
export function getBasicDealPricingStatusForDealManager(
  deal: DealVersion
): BasicDealPricingStatus {
  const preciseStatus = getPreciseDealPricingStatus(deal);

  if (preciseStatus === PreciseDealPricingStatus.Pending) {
    return PreciseDealPricingStatus.Live;
  }

  return preciseStatus;
}

export function getPreciseDealPricingStatus(
  deal: DealVersion,
  nowInput?: MomentInput
): PreciseDealPricingStatus {
  const now = moment(nowInput).local();

  return DealTypeVersion.isCompetitive(deal)
    ? getDealPricingStatusForCompetitive(deal, now)
    : getDealPricingStatusForNegotiated(deal, now);
}

function getDealPricingStatusForCompetitive(deal: DealVersion, today?: Moment) {
  if (!deal.saleDate) {
    return PreciseDealPricingStatus.Expected;
  }

  const comparableSaleDate = dateForLocalComparison(deal.saleDate, today);
  const isInFuture = comparableSaleDate.isAfter(today);
  const isToday = comparableSaleDate.isSame(today, 'day');

  if (isInFuture) {
    return isToday
      ? PreciseDealPricingStatus.Live
      : PreciseDealPricingStatus.Expected;
  }

  const isThisWeek = comparableSaleDate.isSame(today, 'week');
  if (isThisWeek || isTradedCurrentOrFutureWeek(deal, today)) {
    return hasAnnouncedWinner(deal)
      ? PreciseDealPricingStatus.Sold
      : PreciseDealPricingStatus.Pending;
  }

  return PreciseDealPricingStatus.Closed;
}

function getDealPricingStatusForNegotiated(
  deal: DealVersion,
  now: Moment
): PreciseDealPricingStatus {
  const isOrderPeriod = !!deal.orderPeriod && !!deal.orderPeriod.beginDate;
  const orderPeriodInFuture =
    isOrderPeriod && deal.orderPeriod.beginDate.isAfter(now);

  if (
    (deal.isTBD ||
      deal.isDayToDay ||
      !isOrderPeriod ||
      orderPeriodInFuture ||
      orderPeriodIsStale(deal)) &&
    !deal.pricingCommitted
  ) {
    return PreciseDealPricingStatus.Expected;
  }
  const isRepricingOrderPeriod =
    !!deal.repricingOrderPeriod && !!deal.repricingOrderPeriod.endDate;
  const repricingOrderPeriodIsOpen =
    isRepricingOrderPeriod && deal.repricingOrderPeriod.endDate.isAfter(now);

  if (
    isOrderPeriod &&
    !orderPeriodInFuture &&
    (!deal.pricingCommitted || repricingOrderPeriodIsOpen)
  ) {
    return PreciseDealPricingStatus.Live;
  }

  if (
    deal.pricingCommitted &&
    (!isRepricingOrderPeriod || !repricingOrderPeriodIsOpen) &&
    isTradedThisWeek(deal, now)
  ) {
    return PreciseDealPricingStatus.Priced;
  }

  return PreciseDealPricingStatus.Closed;
}

function orderPeriodIsStale(deal: DealVersion): boolean {
  const isOrderPeriod = !!deal.orderPeriod && !!deal.orderPeriod.beginDate;
  const orderPeriodBegin = isOrderPeriod ? deal.orderPeriod.beginDate : null;

  return (
    !!orderPeriodBegin &&
    !!deal.expectedPricingDate &&
    orderPeriodBegin.isBefore(
      deal.isWeekOf
        ? deal.expectedPricingDate
        : subtractBusinessDays(deal.expectedPricingDate, 2)
    )
  );
}

function subtractBusinessDays(date: Moment, days: number): Moment {
  const newDate = date.clone();

  for (let i = 0; i < days; i++) {
    // If it's Monday, subtract 3 days to skip the weekend.
    const daysToSubtract = newDate.day() === 1 ? 3 : 1;
    newDate.subtract(daysToSubtract, 'days');
  }

  return newDate;
}

function isTradedThisWeek(deal: DealVersion, now: Moment): boolean {
  return deal.series.some((series) =>
    series.initialTradeDate ? series.initialTradeDate.isSame(now, 'week') : true
  );
}

export function isTradedCurrentOrFutureWeek(
  deal: DealVersion,
  now: Moment
): boolean {
  return deal.series.some(
    (series) =>
      series.initialTradeDate &&
      series.initialTradeDate.isSameOrAfter(now, 'week')
  );
}

export function tradedOnWhichDay(deal: DealVersion, now: Moment): any {
  if (deal.isWeekOf) {
    return `Week of ${deal.expectedPricingDate
      .startOf('week')
      .add(1, 'days')
      .format('M/DD/YYYY')}`;
  } else {
    const comparableSaleDate = dateForLocalComparison(deal.saleDate, now);

    if (!!isTradedThisWeek(deal, now) && !!comparableSaleDate) {
      if (now.isSame(comparableSaleDate, 'day')) {
        return 'Today';
      } else if (now.clone().add(1, 'day').isSame(comparableSaleDate, 'day')) {
        return 'Tomorrow';
      } else if (
        now.isSame(comparableSaleDate, 'week') &&
        comparableSaleDate.isAfter(now)
      ) {
        return comparableSaleDate.format('dddd');
      }
    }

    return null;
  }
}

export function getFinalDateForDeal(deal: DealVersion): string {
  const date =
    (DealTypeVersion.isCompetitive(deal)
      ? deal.saleDate
      : deal.expectedPricingDate) || moment();

  return date && date.format('YYYY-MM-DD');
}

export function dateForLocalComparison(
  date: Moment | undefined,
  now: Moment
): Moment {
  // For a UTC date that has no time, assume it should just be a date, not a datetime
  // Shift to the local timezone for comparisons.
  if (
    date &&
    date.utcOffset() === 0 &&
    date.hours() === 0 &&
    date.minutes() === 0 &&
    date.seconds() === 0
  ) {
    return date.clone().add(-1 * now.utcOffset(), 'minutes');
  } else {
    return date;
  }
}

function mapPendingStatusToBasic(
  deal: DealVersion,
  nowInput?: MomentInput
): BasicDealPricingStatus {
  const now = moment(nowInput).local();

  if (hasAvailableTimeToAnnounceWinner(deal, now)) {
    return PreciseDealPricingStatus.Live;
  }

  return PreciseDealPricingStatus.Sold;
}

function hasAnnouncedWinner(deal: DealVersion) {
  return deal.seniorManager && deal.seniorManager.name;
}

function hasAvailableTimeToAnnounceWinner(deal: DealVersion, now: Moment) {
  const endTimeToSubmitWinner = cloneDeep(deal.saleDate).day('Friday');

  return now.isSameOrBefore(endTimeToSubmitWinner, 'day');
}

export function orderPeriodGetter(
  deal: DealVersion,
  currentTime?: Moment
): string {
  const now = currentTime ? currentTime : moment().local();
  let orderPeriod: IssueOrderPeriod.OrderPeriodCountdown;

  if (DealTypeVersion.isNegotiated(deal) || DealTypeVersion.isPrivatePlacement(deal)) {
    if (
      getDealPricingStatusForNegotiated(deal, now) ===
      PreciseDealPricingStatus.Live
    ) {
      orderPeriod = IssueOrderPeriod.getCountdown(deal);
    }

    return orderPeriodTransformer(orderPeriod);
  } else {
    return getDealPricingStatusForCompetitive(deal, now);
  }
}

export function orderPeriodTransformer(
  orderPeriod: IssueOrderPeriod.OrderPeriodCountdown
): string | null {
  switch (!!orderPeriod && orderPeriod.type) {
    case OrderPeriodType.retail:
      return 'Retail';

    case OrderPeriodType.all:
      if (
        orderPeriod.orderTypes &&
        orderPeriod.orderTypes.filter(
          (t) => t.orderType === 'RTL' && t.priority === 1
        ).length
      ) {
        return 'Retail';
      }
      return 'Institutional';

    case OrderPeriodType.institutional:
      return 'Institutional';

    default:
      return null;
  }
}
